"use client";
import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useExtensionStatus } from "@/utils/useExtensionStatus";
import {
  Alert,
  Button,
  Snackbar,
  useMediaQuery,
  Box,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function ExtensionNotifier({
  inline = false,
  inlineInstallAsLink = false,
}) {
  const { installed, checkingComplete, recheckExtension } =
    useExtensionStatus();
  const [showNotification, setShowNotification] = useState(false);
  const theme = useTheme();
  const profile = useSelector((state) => state.user.profile);

  const isLg = useMediaQuery(theme.breakpoints.up("lg"));
  const isXl = useMediaQuery(theme.breakpoints.up("xl"));
  const isMediumOrLarge = isLg && !isXl;

  const isEmailVerified = profile?.email_verified !== false;

  useEffect(() => {
    if (
      checkingComplete &&
      installed === false &&
      isMediumOrLarge &&
      !inline &&
      isEmailVerified
    ) {
      setShowNotification(true);
    }
  }, [installed, checkingComplete, isMediumOrLarge, inline, isEmailVerified]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && !installed) {
        recheckExtension();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [installed, recheckExtension]);

  const handleClose = () => {
    setShowNotification(false);
  };

  if (inline) {
    if (!checkingComplete || installed !== false) {
      return (
        <Box
          sx={{
            width: "100%",
            minHeight: "52px",
            display: "flex",
            alignItems: "center",
          }}
        />
      );
    }

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <Alert
          severity="info"
          sx={{
            width: inlineInstallAsLink ? "fit-content" : "100%",
            mx: inlineInstallAsLink ? "auto" : 0,
            opacity: 0.9,
            fontSize: "0.85rem",
            backgroundColor: "transparent",
            color: theme.palette.text.primary,
            boxShadow: "none",
            border: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: inlineInstallAsLink ? "center" : "flex-start",
            minHeight: "52px",
            "& .MuiAlert-message": {
              fontSize: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: inlineInstallAsLink ? "center" : "flex-start",
              padding: "8px 0",
              textAlign: inlineInstallAsLink ? "center" : "left",
            },
            "& .MuiAlert-icon": {
              color: theme.palette.primary.main,
              marginRight: "12px",
              display: "flex",
              alignItems: "center",
              alignSelf: "center",
              padding: 0,
            },
            "& .MuiAlert-action": {
              padding: "0 8px",
              marginRight: 0,
              alignSelf: "center",
            },
          }}
          action={
            inlineInstallAsLink ? null : (
              <Button
                color="primary"
                size="medium"
                variant="contained"
                href="https://chromewebstore.google.com/detail/meex/dnjklpokjknbbjejbpcicakjefigcnda"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  fontWeight: 600,
                  py: 0.1,
                  my: "auto",
                  alignSelf: "center",
                  fontSize: "0.8rem",
                }}
              >
                Install
              </Button>
            )
          }
        >
          {inlineInstallAsLink ? (
            <>
              Search faster on LinkedIn with our extension.{" "}
              <Link
                href="https://chromewebstore.google.com/detail/meex/dnjklpokjknbbjejbpcicakjefigcnda"
                target="_blank"
                rel="noopener noreferrer"
                underline="always"
                sx={{
                  ml: 0.75,
                  fontSize: "inherit",
                  fontWeight: "inherit",
                  color: "inherit",
                }}
              >
                Install
              </Link>
            </>
          ) : (
            "Power your LinkedIn with our extension instantly"
          )}
        </Alert>
      </Box>
    );
  }

  return (
    <Snackbar
      open={
        showNotification &&
        installed === false &&
        isMediumOrLarge &&
        isEmailVerified
      }
      onClose={handleClose}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      sx={{
        top: "16px !important",
        width: { xs: "100%", md: "670px" },
      }}
    >
      <Alert
        onClose={handleClose}
        severity="info"
        sx={{
          width: "100%",
          backgroundColor: "transparent",
          color: theme.palette.text.primary,
          boxShadow: "none",
          border: "none",
          display: "flex",
          alignItems: "center",
          "& .MuiAlert-message": {
            fontSize: "1rem",
            display: "flex",
            alignItems: "center",
            padding: "8px 0",
          },
          "& .MuiAlert-icon": {
            color: theme.palette.primary.main,
            marginRight: "12px",
            display: "flex",
            alignItems: "center",
            alignSelf: "center",
            padding: 0,
          },
          "& .MuiAlert-action": {
            padding: "0 8px",
            marginRight: 0,
            alignSelf: "center",
          },
        }}
        action={
          <Button
            color="primary"
            size="medium"
            variant="contained"
            href="https://chromewebstore.google.com/detail/meex/dnjklpokjknbbjejbpcicakjefigcnda"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              backgroundColor: theme.palette.primary.main,
              "&:hover": {
                backgroundColor: theme.palette.primary.dark,
              },
              fontWeight: 600,
              py: 0.35,
              my: "auto",
              alignSelf: "center",
              fontSize: "0.90rem",
            }}
          >
            Install
          </Button>
        }
      >
        Instantly request meetings on LinkedIn with our extension
      </Alert>
    </Snackbar>
  );
}
